import urlMixin from '../../mixins/url.js';
import officeInfo from '../glb/data.brokerage.js';
import states from '../glb/data.state.js';

const getUrlType = (isAddress, geo) => {
    if (isAddress) {
        if (geo.city) {
            return 'city';
        }
        return 'zipcode';
    } else {
        if (geo.zipcode) {
            return 'zipcode';
        }
        if (geo.county && !geo.city) {
            return 'county';
        }
        if (geo.state && !geo.city) {
            return 'state';
        }
    }
    return 'city';
};

export default function ({ glb = {}, geo = {}, molang }) {
    const appUrl = glb.appUrl;

    const generateBuyerMenu = () => {
        const defaultLinks = [
            {
                text: molang('header.GetPreApproved'),
                href: appUrl + 'preapproval/',
            },
            {
                text: molang('header.GetInsuranceQuotes'),
                href: appUrl + 'insurance/' + (geo.zipcode ? `?zip=${geo.zipcode}` : ''),
            },
            {
                text: molang('header.HomeBuyingChecklist'),
                href: appUrl + 'blog/home-buyer-checklist-11-basic-steps-to-buying-a-home/',
            },
            {
                text: molang('header.veteranResources'),
                href: appUrl + 'veterans/',
            },
            {
                text: molang('header.allHomes'),
                href: appUrl + 'sitemap/',
            },
        ];

        const filters = ['forSale', 'singleFamily', 'condo', 'multiFamily', 'land', 'openHouse'];
        const templateMap = {
            condo: 'header.ApartmentsForSale',
            singleFamily: 'header.HousesForSale',
            multiFamily: 'header.multiFamily',
            land: 'header.land',
            forSale: 'header.HomesforSale',
            openHouse: 'header.OpenHouses',
            priceReduce: 'header.RecentPriceReductions',
        };

        if (!geo.state) {
            return [
                {
                    text: molang('header.HomesForSaleNearMe'),
                    href: appUrl + 'for-sale/',
                },
                ...defaultLinks,
            ];
        }

        const isAddress = geo.geoType === 'ADDRESS';
        const baseName = (isAddress ? geo.city || geo.zipcode : geo.zipcode || geo.city || geo.county) || geo.state;

        const templateObj = {};
        Object.entries(templateMap).forEach(([key, template]) => {
            templateObj[key] = {
                text: molang(template, { baseName }),
            };
        });

        if (geo.zipcode || geo.city || geo.county) {
            const mapUrl = urlMixin.methods.generateMapSearchUrl(appUrl, {
                type: getUrlType(isAddress, geo),
                cityName: geo.city,
                stateCode: geo.stateCode,
                zipcode: geo.zipcode,
                countyCode: geo.countyCode,
                filterArray: filters,
                listingCountObj: glb.listingCountObj,
            });

            const filtered = filters.flatMap((item) => {
                const count = item === 'forSale' ? mapUrl.baseCount : mapUrl.filteredUrlObj?.[`${item}Count`];
                if (!count) return [];
                return [
                    {
                        text: templateObj[item].text,
                        href: item === 'forSale' ? mapUrl.base : mapUrl.filteredUrlObj[item],
                    },
                ];
            });

            return [
                {
                    text: molang('header.HomesForSaleNearMe'),
                    href: appUrl + 'for-sale/',
                },
                ...filtered,
                ...defaultLinks,
            ];
        }

        let menu = [{
            text: molang('header.HomesForSaleNearMe'),
            href: appUrl + 'for-sale/',
        }];

        if (glb.splits['movoto-sitemap-reorg-CW-4136'] != 'on') {
            const stateMspBasePath = geo.stateCode ? `${geo.stateCode}/` : '';
            menu.push({
                text: templateObj.forSale.text,
                href: appUrl + stateMspBasePath,
            });
            menu.push({
                text: templateObj.openHouse.text,
                href: appUrl + stateMspBasePath + 'open-houses/',
            });
            menu.push({
                text: templateObj.priceReduce.text,
                href: appUrl + stateMspBasePath + 'reduced-30/',
            });
        }
        menu.push(...defaultLinks);
        return menu;
    };

    const generateRentalMenu = () => {
        const list = [
            {
                text: molang('header.HomesForRentNearMe'),
                href: appUrl + 'rentals/',
            },
        ];

        if (!geo.state) {
            return {
                text: molang('header.Rent'),
                class: 'rent',
                list: [
                    ...list,
                    {
                        text: molang('header.HousesForRentNearMe'),
                        href: appUrl + 'rentals/single-family/',
                    },
                    {
                        text: molang('header.ApartmentsForRentNearMe'),
                        href: appUrl + 'rentals/condos/',
                    },
                ],
            };
        }

        const isAddress = geo.geoType === 'ADDRESS';
        const baseName = geo.city || geo.zipcode || geo.county || geo.state;
        const templateObj = {
            rentalHomes: molang('header.RentalHomes', { baseName }),
            singleFamilyRent: molang('header.HousesForRent', { baseName }),
            condoRent: molang('header.ApartmentsForRent', { baseName }),
        };

        const mapUrl = urlMixin.methods.generateMapSearchUrl(appUrl, {
            type: getUrlType(isAddress, geo),
            cityName: geo.city,
            stateCode: geo.stateCode,
            zipcode: geo.zipcode,
            countyCode: geo.countyCode,
            filterArray: ['singleFamily', 'condo'],
            listingCountObj: glb.listingCountObj,
            rentals: true,
        });

        const filters = ['rentalHomes', 'singleFamily', 'condo'].map((item) => {
            const labelKey = item + (['singleFamily', 'condo'].includes(item) ? 'Rent' : '');
            return {
                text: templateObj[labelKey],
                href: mapUrl.filteredUrlObj?.[item] || mapUrl.base,
            };
        });

        return {
            text: molang('header.Rent'),
            class: 'rent',
            list: [...list, ...filters],
        };
    };

    const generateMortgageMenu = () => ({
        text: molang('header.Mortgage'),
        href: appUrl + 'mortgages/',
        list: [
            {
                text: molang('header.MortgageLoan'),
                href: appUrl + 'mortgages/',
            },
            {
                text: molang('header.GetPreApproved'),
                href: appUrl + 'preapproval/',
            },
            {
                text: molang('header.BuyingPower'),
                href: appUrl + 'mortgages/buyingpower/',
            },
            {
                text: molang('header.MortgageCalculator'),
                href: appUrl + 'mortgages/calculator/',
            },
            {
                text: molang('header.MortgageRates'),
                href: appUrl + 'mortgages/rates/',
            },
            {
                text: molang('header.RefinanceRates'),
                href: appUrl + 'mortgages/refinance/',
            },
            {
                text: molang('header.veteranMortgage'),
                href: appUrl + 'veterans/',
            },
        ],
    });

    const generateSellerMenu = () => {
        const list = [
            {
                text: molang('header.sellHome'),
                href: appUrl + 'sell/',
            },
            {
                text: molang('header.CashOffer'),
                href: appUrl + 'sell-fast/',
            },
        ];

        list.push(
            {
                text: molang('header.HomeSaleCalculator'),
                href: appUrl + 'home-sale-proceeds-calculator/',
            },
            {
                text: molang('header.GetaHomeValuation'),
                href: appUrl + 'what-is-your-home-worth/',
            },
            {
                text: molang('header.ownerDashBoard'),
                href: appUrl + 'homeowner/',
            },
            {
                text: molang('header.HomeRepairServiceProviders'),
                href: appUrl + 'homeservices/',
            }
        );

        return {
            text: molang('header.Sell'),
            list,
        };
    };

    const generateAgentMenu = () => {
        const isMyAgent = glb.agent?.id;
        const baseLink = isMyAgent ? 'my-movoto/myagent/' : 'agents/';
        const list = [];

        if (!isMyAgent) {
            list.push({
                text: molang('header.MeetanAgent'),
                href: appUrl + 'agents/',
            });

            if (geo.state && glb.splits['movoto-sitemap-reorg-CW-4136'] != 'on') {
                if (geo.city) {
                    list.push({
                        text: molang('header.AgentsSearch', { baseName: geo.city }),
                        href: appUrl + `agents/${geo.cityCode}-${geo.stateCode}/`,
                    });
                } else if (geo.county) {
                    list.push({
                        text: molang('header.AgentsSearch', { baseName: geo.county }),
                        href: appUrl + `agents/${geo.countyCode}-${geo.stateCode}/`,
                    });
                }
            }

            const office = officeInfo[`${geo.cityCode}-${geo.stateCode}`];
            if (office) {
                list.push({
                    text: molang('header.brokerageoffices', { baseName: geo.city }),
                    href: appUrl + `brokerage/${geo.cityCode}-${geo.stateCode}/`,
                });
            }

            list.push({
                text: molang('header.CreateanAgentProfile'),
                href: 'https://agents.movoto.com/platforms/',
                rel: 'nofollow',
            });
        }

        return {
            text: isMyAgent ? molang('header.myAgent') : molang('header.Agent'),
            href: appUrl + baseLink,
            list: isMyAgent ? null : list,
            class: 'agent',
        };
    };

    return [{ text: molang('header.Buy'), list: generateBuyerMenu() }, generateSellerMenu(), generateRentalMenu(), generateMortgageMenu(), generateAgentMenu()];
}
